import { useEffect, useState } from "react"

import { useTranslation } from "react-i18next"

import type { DirectorySearchResult } from "@/services/directory-search-service"

import { DirectorySearch } from "@/components/directory-search/directory-search"
import { Minus, Plus, X } from "@/components/icons"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { Spinner } from "@/components/ui/spinner"
import { Textarea } from "@/components/ui/textarea"

import { formatSekAmount } from "./fair-use-ops-utils"

const MIN_TOP_UPS = 1
const MAX_TOP_UPS = 12

export type FairUseTopUpTarget = {
  userId: string
  label: string
}

type FairUseTopUpDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  /**
   * Pre-chosen recipient when the dialog is opened from a row menu. `null`
   * opens the people picker — there is no third path, and in particular no
   * field to paste an oid into: that was how the old grant dialog let an admin
   * bill the wrong person with one typo.
   */
  target: FairUseTopUpTarget | null
  blockCapSek: number
  blockListPriceSek?: number
  isPending: boolean
  onSubmit: (args: { userId: string; blocks: number; reason?: string }) => void
}

export function FairUseTopUpDialog({
  open,
  onOpenChange,
  target,
  blockCapSek,
  blockListPriceSek,
  isPending,
  onSubmit,
}: FairUseTopUpDialogProps) {
  const { t, i18n } = useTranslation()
  const locale = i18n.language
  const [picked, setPicked] = useState<FairUseTopUpTarget | null>(target)
  const [blocks, setBlocks] = useState(MIN_TOP_UPS)
  const [reason, setReason] = useState("")

  useEffect(() => {
    if (!open) return
    setPicked(target)
    setBlocks(MIN_TOP_UPS)
    setReason("")
  }, [open, target])

  const handleSelect = (result: DirectorySearchResult) => {
    setPicked({ userId: result.oid, label: result.displayName || result.email || result.oid })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("adminDashboard.fairUse.ops.topUpTitle", "Top up a user")}</DialogTitle>
          <DialogDescription>
            {t(
              "adminDashboard.fairUse.ops.topUpDescription",
              "Add budget for the rest of the month. Every top-up is billed."
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>{t("adminDashboard.fairUse.ops.topUpUserLabel", "User")}</Label>
            {picked ? (
              <div className="flex items-center justify-between rounded-md border px-3 py-2 text-sm">
                <span className="truncate font-medium">{picked.label}</span>
                {/* Pre-chosen from a row menu stays fixed: the admin opened the
                    menu on a specific person, so silently swapping the target
                    inside the dialog would be the same mis-billing risk. */}
                {!target && (
                  <button
                    className="text-muted-foreground hover:text-foreground"
                    type="button"
                    aria-label={t(
                      "adminDashboard.fairUse.ops.topUpClearUser",
                      "Choose someone else"
                    )}
                    onClick={() => setPicked(null)}
                  >
                    <X className="size-4" />
                  </button>
                )}
              </div>
            ) : (
              <DirectorySearch
                admin
                types={["users"]}
                placeholder={t(
                  "adminDashboard.fairUse.ops.topUpUserPlaceholder",
                  "Search for a user…"
                )}
                onSelect={handleSelect}
              />
            )}
          </div>

          <div className="space-y-2">
            <Label>{t("adminDashboard.fairUse.ops.topUpCountLabel", "Top-ups")}</Label>
            <div className="flex items-center gap-3">
              <Button
                aria-label={t("adminDashboard.fairUse.ops.topUpFewer", "Fewer top-ups")}
                disabled={blocks <= MIN_TOP_UPS}
                size="icon"
                type="button"
                variant="outline"
                onClick={() => setBlocks((current) => Math.max(MIN_TOP_UPS, current - 1))}
              >
                <Minus className="size-4" />
              </Button>
              <span className="w-10 text-center text-lg font-semibold tabular-nums">{blocks}</span>
              <Button
                aria-label={t("adminDashboard.fairUse.ops.topUpMore", "More top-ups")}
                disabled={blocks >= MAX_TOP_UPS}
                size="icon"
                type="button"
                variant="outline"
                onClick={() => setBlocks((current) => Math.min(MAX_TOP_UPS, current + 1))}
              >
                <Plus className="size-4" />
              </Button>
            </div>
            <div className="bg-muted/50 space-y-1 rounded-xl border p-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">
                  {t("adminDashboard.fairUse.ops.topUpAddedBudget", "Added budget")}
                </span>
                <span className="font-medium tabular-nums">
                  +{formatSekAmount(blocks * blockCapSek, locale)} SEK
                </span>
              </div>
              {blockListPriceSek != null && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">
                    {t("adminDashboard.fairUse.ops.topUpPrice", "Price (billed)")}
                  </span>
                  <span className="font-medium tabular-nums">
                    {formatSekAmount(blocks * blockListPriceSek, locale)} SEK
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="fair-use-top-up-reason">
              {t("adminDashboard.fairUse.ops.topUpReasonLabel", "Reason (optional)")}
            </Label>
            <Textarea
              id="fair-use-top-up-reason"
              value={reason}
              placeholder={t(
                "adminDashboard.fairUse.ops.topUpReasonPlaceholder",
                "Why is this top-up needed?"
              )}
              onChange={(event) => setReason(event.target.value)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button className="h-10" variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.cancel", "Cancel")}
          </Button>
          <Button
            className="h-10"
            disabled={isPending || !picked}
            onClick={() =>
              picked &&
              onSubmit({ userId: picked.userId, blocks, reason: reason.trim() || undefined })
            }
          >
            {isPending ? <Spinner className="mr-2 size-4" /> : null}
            {t("adminDashboard.fairUse.ops.topUpConfirm", "Top up")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
